import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import FallbackSpinner from './@core/components/spinner'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import UserLayout from './layouts/UserLayout'
import BlankLayout from './@core/layouts/BlankLayout'
import AclGuard from './@core/components/auth/AclGuard'
import GuestGuard from './@core/components/auth/GuestGuard'
import { defaultACLObj } from './configs/acl'
import AuthLayout from './layouts/AuthLayout'
import toast from 'react-hot-toast'
import { useAuth } from './hooks/useAuth'
import { Box, Typography } from '@mui/material'
import { getNotificationPermission, messaging } from './notification/firebase'
import { onMessage } from 'firebase/messaging'
import BellOutline from 'mdi-material-ui/BellOutline'
import CookieConsentBanner from 'src/components/CookieConsentBanner'
import GoodsCategoryLabelsBootstrap from 'src/components/GoodsCategoryLabelsBootstrap'

const PRIVACY_POLICY_URL =
  process.env.REACT_APP_PRIVACY_POLICY_URL || 'https://www.oujat.com/privacy-policy-page'

//admin
const HomePage = React.lazy(() => import('./pages/home'))
const DashboardPage = React.lazy(() => import('./pages/dashboard'))
const OrdersPage = React.lazy(() => import('./pages/orders'))
const OrderOverview = React.lazy(() => import('./pages/orderOverview'))
const AssesmentCostDetailPage = React.lazy(() => import('./pages/orderOverview/assesment-cost-deails'))
const CancleOrderPage = React.lazy(() => import('./pages/orderOverview/cancleorder'))
//const FullPaymentPage = React.lazy(() => import('./pages/fullPayment'))
const Payments = React.lazy(() => import('./pages/payments'))
const CreateQMSPage = React.lazy(() => import('./pages/qms/create-new-qms'))
const ServiceCategoryOptionList = React.lazy(() => import('./pages/qms/ServiceCategoryOptionList'))
const LoginPage = React.lazy(() => import('./pages/login'))
const VerifyOTPPage = React.lazy(() => import('./pages/registration/verify-otp'))
const ShipperAccountType = React.lazy(() => import('./pages/registration/shipperaccounttype'))
const RegistrationPage = React.lazy(() => import('./pages/registration/registration'))
const UpdateProfilePage = React.lazy(() => import('./pages/update-profile'))
const Page401 = React.lazy(() => import('./pages/401'))
const Page404 = React.lazy(() => import('./pages/404'))
const ForgotPasswordPage = React.lazy(() => import('./pages/forgotPassword'))
const VerifyOtpPage = React.lazy(() => import('./pages/verifyOtp'))
const ResetPasswordPage = React.lazy(() => import('./pages/resetPassword'))
const QMSPage = React.lazy(() => import('./pages/qms'))
// const AddressBookForm = React.lazy(() => import('./pages/qms/addressbookform'))
const TeamManagementPage = React.lazy(() => import('./pages/team-management'))
const AssignTeamRole = React.lazy(() => import('./pages/assign-role'))
const AssignPermissions = React.lazy(() => import('./pages/assign-permissions'))
const QmsDetailPage = React.lazy(() => import('./pages/qms/qmsqetails'))
const CompareQuotesDetails = React.lazy(() => import('./pages/qms/comparedetails'))
const ServicePage = React.lazy(() => import('./pages/services'))
const WithProtectionServicedetail = React.lazy(() => import('./pages/services/withprotectionservicedetail'))
const WithoutProtectionServicedetail = React.lazy(() => import('./pages/services/withoutprotectionservicedetail'))
const NotificationPage = React.lazy(() => import('./pages/notification'))
const DisputeCenterPage = React.lazy(() => import('./pages/dispute-center'))
const DisputeOrderList = React.lazy(() => import('./pages/dispute-center/DisputeOrderList'))
const PaymentGateway = React.lazy(() => import('./pages/payment-gateway'))
const CompanyFrontPage = React.lazy(() => import('./pages/companyfrontpage'))
const ChatPage = React.lazy(() => import('./pages/chat'))
const PyamentSuccessPage = React.lazy(() => import('./pages/PaymentSuccess'))
const PaymentFailed = React.lazy(() => import('./pages/paymentFailed'))
const HelpChatPage = React.lazy(() => import('./pages/helpChat'))
const OrderCompletedPage = React.lazy(() => import('./pages/orderOverview/OrderCompletedPage'))
const ProfilePage = React.lazy(() => import('./pages/profile'))
const SuscriptionsPage = React.lazy(() => import('./pages/subscriptions/SuscriptionPlan'))
const CustomizablePlanPage = React.lazy(() => import('./pages/subscriptions/CustomizablePlanPage'))
const ManageSubscriptionPage = React.lazy(() => import('./pages/subscriptions/ManageSubscription'))
const AddressBookPage = React.lazy(() => import('./pages/address-book/AddressBook'))
const AddPersonalContactPage = React.lazy(() => import('./pages/address-book/contact-type/PersonalContact'))
const AddBusinessContactPage = React.lazy(() => import('./pages/address-book/contact-type/BusinessContact'))
const TermsAndConditionsPage = React.lazy(() => import('./pages/terms-and-conditions'))

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>
  }
}

function App() {
  const aclAbilities = defaultACLObj
  const { setFcmToken } = useAuth()
  useEffect(() => {
    getNotificationPermission(setFcmToken)
    onMessage(messaging, payload => {
      // getUnReadNotificationCount()
      toast(
        t => (
          <Box display={'flex'} alignItems={'flex-start'} gap={3}>
            <BellOutline />
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, lineHeight: '25px', textTransform: 'capitalize' }}>
                {payload.notification.title}
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '15px' }}>
                {payload.notification.body}
              </Typography>
            </Box>
          </Box>
        ),
        {
          style: { border: '1px solid #FB3601', borderRadius: '12px' },
          position: 'top-right',
          duration: 3000
        }
      )
    })
  }, [])
  return (
    <>
      <Suspense fallback={<FallbackSpinner />}>
        <AclGuard aclAbilities={aclAbilities}>
          <Routes>
          <Route
            element={
              <BlankLayout>
                <Outlet />
              </BlankLayout>
            }
          >
            <Route path='/401' element={<Page401 />} />
            <Route path='/404' element={<Page404 />} />
            <Route path='/payment-success' element={<PyamentSuccessPage />} />
            <Route path='/payment-cancel' element={<PaymentFailed />} />
            <Route path='/help' element={<HelpChatPage />} />
            <Route path='/terms-and-conditions' element={<TermsAndConditionsPage />} />

            <Route
              element={
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              }
            >
              <Route
                element={
                  <Guard guestGuard>
                    <Outlet />
                  </Guard>
                }
              >
                <Route path='/login' element={<LoginPage />}></Route>
                <Route path='/forgot-password' element={<ForgotPasswordPage />}></Route>
                <Route path='/reset-password' element={<ResetPasswordPage />} />
                <Route path='/account-type' element={<ShipperAccountType />} />
                <Route path='/register' element={<RegistrationPage />} />
                <Route path='/verify-otp' element={<VerifyOtpPage />}></Route>
                <Route path='/otp-verification' element={<VerifyOTPPage />} />
                <Route path='/update-profile' element={<UpdateProfilePage />} />
              </Route>
            </Route>
            <Route path='/scriptions-plan' element={<SuscriptionsPage />} />
            <Route path='/scriptions-plan/upgrade-plan/:planId' element={<CustomizablePlanPage />} />
          </Route>

          <Route
            element={
              <UserLayout>
                <Outlet />
              </UserLayout>
            }
          >
            <Route
              element={
                <Guard authGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route path='' element={<HomePage />} />
              <Route path='/dashboard' element={<DashboardPage />} />
              <Route path='/orders' element={<OrdersPage />} />
              <Route path='/orders/orderoverview/:id' element={<OrderOverview />} />
              <Route path='/orders/orderoverview/assesment-cost-details/:id' element={<AssesmentCostDetailPage />} />
              <Route path='/orders/cancle-order' element={<CancleOrderPage />} />
              <Route path='/payments' element={<Payments />} />
              <Route path='/qms' element={<QMSPage />} />
              {/* <Route path='/qms/create-qms/add-address-form' element={<AddressBookForm />} /> */}
              <Route path='/team-management' element={<TeamManagementPage />} />
              <Route path='/team-management/assign-role' element={<AssignTeamRole />} />
              <Route path='/team-management/assign-role/:id' element={<AssignTeamRole />} />
              <Route path='/team-management/assign-permissions' element={<AssignPermissions />} />
              <Route path='/team-management/assign-permissions/:id' element={<AssignPermissions />} />
              <Route path='/qms/selected-category' element={<ServiceCategoryOptionList />}></Route>
              <Route path='/qms/create-qms' element={<CreateQMSPage />} />
              <Route path='/qms/detail/:id' element={<QmsDetailPage />} />
              <Route path='/qms/compare-quotes' element={<CompareQuotesDetails />} />
              <Route path='/services' element={<ServicePage />} />
              <Route path='/services/with-protections-service-detail/:id' element={<WithProtectionServicedetail />} />
              <Route
                path='/services/without-protections-service-detail/:id'
                element={<WithoutProtectionServicedetail />}
              />
              <Route path='/notification' element={<NotificationPage />} />
              <Route path='/dispute-center' element={<DisputeOrderList />} />
              <Route path='/dispute-center/create-dispute' element={<DisputeCenterPage />} />
              <Route path='/payment-gateway' element={<PaymentGateway />} />
              <Route path='/services/storefront/:userId' element={<CompanyFrontPage />} />
              <Route path='/chat' element={<ChatPage />} />
              <Route path='/orders/order-completed/:id' element={<OrderCompletedPage />} />
              <Route path='/address-book' element={<AddressBookPage />} />
              <Route path='/address-book/add-personal-contact' element={<AddPersonalContactPage />} />
              <Route path='/address-book/add-business-contact' element={<AddBusinessContactPage />} />
              <Route path='/subscription-management' element={<ManageSubscriptionPage />} />
            </Route>
            <Route path='/profile' element={<ProfilePage />} />
          </Route>

          {/* If no route found redirect it to --> /404 */}
          <Route path='*' element={<Navigate to='/404' replace />} />
          </Routes>
        </AclGuard>
      </Suspense>
      <CookieConsentBanner privacyHref={PRIVACY_POLICY_URL} />
      <GoodsCategoryLabelsBootstrap />
    </>
  )
}

export default App
